/**
 * Level-kind palette (design §17) — the game's 3Cs archetype provides the DEFAULT level
 * kind, but every level picks its own kind at creation (a Zelda-like mixes topdown
 * dungeons with a data-table bestiary; a SHMUP mixes spline timelines with stat tables).
 * `threeCsForKind` supplies a usable feel contract when a level's kind differs from the
 * game's authored archetype: the authored profile when it matches, sensible defaults
 * otherwise (tune later in the 3Cs editor).
 */
import type { ThreeCsProfile, TopdownThreeCs, DataThreeCs } from '@volley/crucible-levels';

export type LevelKindChoice = 'spatial-2d' | 'topdown-2d' | 'data-table' | 'board-grid';

export const LEVEL_KIND_LABELS: Record<LevelKindChoice, string> = {
  'spatial-2d': 'Side-scroll track',
  'topdown-2d': 'Top-down floor plan',
  'data-table': 'Data (rows/tables)',
  'board-grid': 'Board (paintable grid)',
};

/** Compact variant for chips/nav prefixes ("spatial-2d" means nothing to a designer). */
export const LEVEL_KIND_SHORT: Record<LevelKindChoice, string> = {
  'spatial-2d': 'Track',
  'topdown-2d': 'Floor plan',
  'data-table': 'Table',
  'board-grid': 'Board',
};

/** Human label for any kind id (unknown/future kinds fall back to the raw id). */
export function kindShortLabel(kind: string): string {
  return isKindChoice(kind) ? LEVEL_KIND_SHORT[kind] : kind;
}

// TEMP default feel profile so spatial-2d validation runs before an authored
// `three-cs.json` exists (Bumble's numbers). Data-table levels ignore this.
export const DEFAULT_THREE_CS: ThreeCsProfile = {
  archetype: 'autorunner-2d',
  character: {
    autoRunSpeed: 6,
    backpedalSpeed: -3,
    jumpHeight: 3,
    jumpAirTime: 0.7,
    downedDragSpeedPenalty: 0.4,
    bodyWidth: 0.6,
  },
  tether: { members: 4, restLength: 4, maxStretch: 8 },
};

/** Default top-down feel (StarTropics-like guesses) — used when the archetype flips. */
export const DEFAULT_TOPDOWN_THREE_CS: TopdownThreeCs = {
  archetype: 'topdown-2d',
  character: { moveSpeed: 5, accel: 40, bodyRadius: 0.4 },
  camera: { viewW: 16, viewH: 9 },
};

/** Data / property-driven game (match-3 boards, loot tables) — no kinematics to contract. */
export const DEFAULT_DATA_THREE_CS: DataThreeCs = { archetype: 'data-table' };

export function isKindChoice(kind: string): kind is LevelKindChoice {
  return (
    kind === 'spatial-2d' || kind === 'topdown-2d' || kind === 'data-table' || kind === 'board-grid'
  );
}

/**
 * The level kinds currently OFFERED for authoring. TEMPORARY scope narrowing: we are
 * sticking to side-view (`spatial-2d`) + data (`data-table`) for now, so `topdown-2d` and
 * `board-grid` are disabled — the kinds' model/schema/validation still ship (a level already
 * authored in a disabled kind still loads/renders), they're just not offered as new choices,
 * and level generation coerces a proposed disabled kind to the default. Re-enable by adding a
 * kind back to this list — the single source of truth for what the design tab presents.
 */
export const AVAILABLE_LEVEL_KINDS: readonly LevelKindChoice[] = ['spatial-2d', 'data-table'];

/** Whether a kind is currently offered for authoring (see {@link AVAILABLE_LEVEL_KINDS}). */
export function isKindEnabled(kind: string): boolean {
  return isKindChoice(kind) && AVAILABLE_LEVEL_KINDS.includes(kind);
}

/** Coerce a (possibly disabled or unknown) kind to an offered one — disabled/unknown → side-view. */
export function coerceEnabledKind(kind: string): LevelKindChoice {
  return isKindEnabled(kind) ? (kind as LevelKindChoice) : 'spatial-2d';
}

/** The level kind a 3Cs archetype creates by default. */
export function kindForArchetype(archetype: ThreeCsProfile['archetype']): LevelKindChoice {
  return archetype === 'topdown-2d'
    ? 'topdown-2d'
    : archetype === 'data-table'
      ? 'data-table'
      : 'spatial-2d';
}

/** The 3Cs a level of `kind` validates/generates against: the game's authored profile
 *  when the archetype matches, the built-in default for that kind otherwise. */
export function threeCsForKind(kind: LevelKindChoice, gameThreeCs: ThreeCsProfile): ThreeCsProfile {
  // board-grid has no kinematics — like data-table, any profile satisfies it; keep
  // the game's own profile when it's already the data archetype.
  if (kind === 'board-grid') {
    return gameThreeCs.archetype === 'data-table' ? gameThreeCs : DEFAULT_DATA_THREE_CS;
  }
  if (kindForArchetype(gameThreeCs.archetype) === kind) return gameThreeCs;
  return kind === 'topdown-2d'
    ? DEFAULT_TOPDOWN_THREE_CS
    : kind === 'data-table'
      ? DEFAULT_DATA_THREE_CS
      : DEFAULT_THREE_CS;
}
